import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Link,
    Paper,
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ReplayIcon from '@mui/icons-material/Replay';
import SpeedIcon from '@mui/icons-material/Speed';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { apiString as API_URL } from '../../utils/constants';

interface RateLimitStatus {
    providerId: string;
    tokensUsedLastMinute: number;
    maxTokensPerMinute: number;
    tpmUtilizationPercent: number;
    requestsLastMinute: number;
    maxRequestsPerMinute: number;
    rpmUtilizationPercent: number;
    tokensUsedThisMonth: number;
    maxTokenBudgetPerMonth: number;
    monthlyBudgetUtilizationPercent: number;
    effectiveSource: string;
}

interface ChatErrorDetails {
    title: string;
    message: string;
    category?: string;
    reason?: string;
    confidence?: number;
    retryAfterSeconds?: number;
    code?: number;
    query?: string;
}

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    citations?: Citation[];
    isError?: boolean;
    errorDetails?: ChatErrorDetails;
}

// ponytail: native crypto.randomUUID not typed in older TS lib defs
const newMsgId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readAuthToken = (): string | null => {
    try {
        const raw = localStorage.getItem('auth');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.AccessToken ?? null;
    } catch {
        return null;
    }
};

interface Citation {
    title: string;
    source: string;
    url: string;
    excerpt: string;
}

interface IndexProgress {
    total: number;
    processed: number;
    stage: string;
    isComplete: boolean;
    hasError: boolean;
}

interface ChatSuggestions {
    quickAsks: string[];
    recentEvents: string[];
}

interface ChatSession {
    sessionId: string;
    title: string;
    lastUpdatedTime: string | null;
}

const formatSessionDate = (raw: string | null): string => {
    if (!raw) return '';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    const today = new Date();
    const isToday =
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatCategoryLabel = (cat?: string): string => {
    if (!cat) return 'Out of Scope';
    switch (cat.toLowerCase().trim()) {
        case 'general_chitchat':
            return 'General Chitchat / Off-Topic';
        case 'coding_help':
            return 'Programming / Technical Assistance';
        case 'jailbreak':
            return 'Security / Jailbreak Attempt';
        case 'billing_legal':
            return 'Billing / Legal Advice';
        case 'other_member':
            return 'Other Member Context';
        case 'prescribing':
            return 'Prescribing / Dosing Decision';
        case 'unknown':
            return 'Unrecognized / General Scope';
        default:
            return cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
};

const parseAiError = (err: any, query?: string): ChatErrorDetails => {
    const status = err?.response?.status;
    const data = err?.response?.data;

    let title = 'Message Delivery Failed';
    let message = 'An error occurred while processing your request. Please try again.';
    let category: string | undefined;
    let reason: string | undefined;
    let confidence: number | undefined;
    let retryAfterSeconds: number | undefined;

    if (status === 422 || data?.category || data?.reason || data?.Category || data?.Reason) {
        title = 'Blocked by AI Guardrails (Out of Scope)';
        category = data?.category ?? data?.Category;
        reason = data?.reason ?? data?.Reason;
        confidence = data?.confidence ?? data?.Confidence;
        const mainErr = typeof data === 'string' ? data : (data?.error ?? data?.Message);
        message = mainErr || 'Your question appears to be outside the allowed clinical scope of CHP Care Central.';
    } else if (status === 429) {
        title = 'AI Rate Limit Exceeded';
        retryAfterSeconds = data?.retryAfterSeconds ?? data?.RetryAfterSeconds;
        const isBudget = data?.isBudgetExhausted ?? data?.IsBudgetExhausted;
        if (isBudget) {
            message = data?.error || 'Your monthly AI token usage limit has been reached.';
        } else if (retryAfterSeconds) {
            message = data?.error || `Rate limit reached. Please wait ${retryAfterSeconds} second(s) before trying again.`;
        } else {
            message = data?.error || 'Rate limit reached. Please wait a moment before trying again.';
        }
    } else if (status === 503) {
        title = 'AI Service Unavailable';
        message = typeof data === 'string' ? data : (data?.error ?? data?.Message ?? 'The CHP AI Chat service is currently unavailable or disabled by an administrator.');
    } else if (status === 401) {
        title = 'Session Unauthorized';
        message = 'Your session may have expired. Please re-authenticate or refresh the page.';
    } else if (status === 400) {
        title = 'Invalid Prompt Request';
        message = typeof data === 'string' ? data : (data?.error ?? data?.Message ?? 'The request parameter or prompt query was invalid.');
    } else if (status === 404) {
        title = 'Endpoint Not Found (404)';
        message = typeof data === 'string' ? data : (data?.error ?? data?.Message ?? 'The AI streaming endpoint (/api/chpai/ask/stream) was not found. Please restart the backend server.');
    } else if (typeof data === 'string' && data.trim()) {
        message = data;
    } else if (data?.error || data?.message || data?.Message) {
        message = data.error || data.message || data.Message;
    } else if (err?.message) {
        message = err.message;
    }

    return {
        title,
        message,
        category,
        reason,
        confidence,
        retryAfterSeconds,
        code: status,
        query,
    };
};

const MemberChatPage = () => {
    const { memberId } = useParams<{ memberId: string }>();
    const { chartDetails } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const { authenticatedUser } = useSelector((state: RootState) => state.authentication);
    const hasReindexPermission = authenticatedUser?.Permissions?.includes('AiForceReindex');

    // Convert all-caps name to Title Case (e.g. JOHN -> John)
    const rawName = chartDetails?.FirstName || 'this member';
    const memberName = rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [indexProgress, setIndexProgress] = useState<IndexProgress>({ total: 0, processed: 0, stage: 'Starting...', isComplete: false, hasError: false });
    const [suggestions, setSuggestions] = useState<ChatSuggestions>({ quickAsks: [], recentEvents: [] });
    const [loadingSuggestions, setLoadingSuggestions] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [chatError, setChatError] = useState<ChatErrorDetails | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [instructions, setInstructions] = useState({ global: '', member: '' });
    const [savingSettings, setSavingSettings] = useState(false);
    const [isAiEnabled, setIsAiEnabled] = useState<boolean>(false);
    const [checkingStatus, setCheckingStatus] = useState<boolean>(true);
    const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitStatus | null>(null);
    const [showTokenMeter, setShowTokenMeter] = useState<boolean>(false);
    const bottomRef = useRef<HTMLDivElement>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    // Abort any in-flight stream when the page unmounts / component tears down
    useEffect(() => () => abortRef.current?.abort(), []);

    const fetchRateLimitStatus = async () => {
        try {
            const res = await axios.get<any>(`${API_URL}/chpai/ratelimit/status`);
            if (res.data) {
                const d = res.data;
                setRateLimitStatus({
                    providerId: d.ProviderId ?? d.providerId ?? '',
                    tokensUsedLastMinute: d.TokensUsedLastMinute ?? d.tokensUsedLastMinute ?? 0,
                    maxTokensPerMinute: d.MaxTokensPerMinute ?? d.maxTokensPerMinute ?? 0,
                    tpmUtilizationPercent: d.TpmUtilizationPercent ?? d.tpmUtilizationPercent ?? 0,
                    requestsLastMinute: d.RequestsLastMinute ?? d.requestsLastMinute ?? 0,
                    maxRequestsPerMinute: d.MaxRequestsPerMinute ?? d.maxRequestsPerMinute ?? 0,
                    rpmUtilizationPercent: d.RpmUtilizationPercent ?? d.rpmUtilizationPercent ?? 0,
                    tokensUsedThisMonth: d.TokensUsedThisMonth ?? d.tokensUsedThisMonth ?? 0,
                    maxTokenBudgetPerMonth: d.MaxTokenBudgetPerMonth ?? d.maxTokenBudgetPerMonth ?? 0,
                    monthlyBudgetUtilizationPercent: d.MonthlyBudgetUtilizationPercent ?? d.monthlyBudgetUtilizationPercent ?? 0,
                    effectiveSource: d.EffectiveSource ?? d.effectiveSource ?? 'Global',
                });
            }
        } catch (e) {
            console.error('Failed to fetch AI rate limit status:', e);
        }
    };

    // Check dynamic AI enablement status
    useEffect(() => {
        const checkStatus = async () => {
            try {
                const res = await axios.get<boolean>(`${API_URL}/chpai/enabled`);
                setIsAiEnabled(res.data);
            } catch (e) {
                console.error('Failed to check AI status:', e);
                setIsAiEnabled(false);
            } finally {
                setCheckingStatus(false);
            }
        };
        checkStatus();
    }, []);

    useEffect(() => {
        if (!isAiEnabled) return;
        fetchRateLimitStatus();

        // ponytail: 30s polling — cheap enough, but 10s doubled search load with no UX benefit
        const intervalId = setInterval(() => {
            fetchRateLimitStatus();
        }, 30000);

        return () => clearInterval(intervalId);
    }, [isAiEnabled]);

    // Fetch dynamic suggestions
    useEffect(() => {
        if (!memberId || !isAiEnabled) return;
        const loadSuggestions = async () => {
            setLoadingSuggestions(true);
            try {
                const res = await axios.get(`${API_URL}/AiIndexing/chat-suggestions/${memberId}?memberName=${encodeURIComponent(memberName)}`);
                const data = res.data;
                setSuggestions({
                    quickAsks: data.QuickAsks ?? data.quickAsks ?? [],
                    recentEvents: data.RecentEvents ?? data.recentEvents ?? [],
                });
            } catch (e) {
                console.error('Failed to load chat suggestions:', e);
                setSuggestions({
                    quickAsks: [
                        `Summarize ${memberName}'s recent assessments`,
                        `What requests are open for ${memberName}?`,
                        `What were ${memberName}'s recent vitals?`
                    ],
                    recentEvents: []
                });
            } finally {
                setLoadingSuggestions(false);
            }
        };
        loadSuggestions();
    }, [memberId, memberName, isAiEnabled]);

    const handleOpenSettings = async () => {
        setSettingsOpen(true);
        try {
            const res = await axios.get(`${API_URL}/chpai/instructions/${memberId}`);
            setInstructions({
                global: res.data.GlobalInstruction ?? res.data.globalInstruction ?? '',
                member: res.data.MemberInstruction ?? res.data.memberInstruction ?? '',
            });
        } catch (e) {
            console.error('Failed to load instructions:', e);
        }
    };

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            await axios.post(`${API_URL}/chpai/instructions/${memberId}`, {
                GlobalInstruction: instructions.global,
                MemberInstruction: instructions.member
            });
            setSettingsOpen(false);
        } catch (e) {
            console.error('Failed to save instructions:', e);
        } finally {
            setSavingSettings(false);
        }
    };

    const refreshSessions = async () => {
        if (!memberId) return;
        try {
            const res = await axios.get(`${API_URL}/chpai/sessions/${memberId}`);
            if (res.data && Array.isArray(res.data)) {
                // Normalize casing from API — handle both Pascal and camel
                const normalized: ChatSession[] = res.data.map((s: any) => ({
                    sessionId: s.SessionId ?? s.sessionId ?? '',
                    title: s.Title ?? s.title ?? 'Untitled Chat',
                    lastUpdatedTime: s.LastUpdatedTime ?? s.lastUpdatedTime ?? null,
                }));
                setSessions(normalized);
            }
        } catch (e) {
            console.error('Failed to load chat sessions:', e);
        }
    };

    // Fetch list of previous sessions on mount
    useEffect(() => {
        if (!isAiEnabled) return;
        refreshSessions();
    }, [memberId, isAiEnabled]);

    const loadSessionHistory = async (targetSessionId: string) => {
        abortRef.current?.abort();
        abortRef.current = null;
        setSessionId(targetSessionId);
        setMessages([]);
        setLoadingHistory(true);
        setChatError(null);
        try {
            const res = await axios.get(`${API_URL}/chpai/history/${targetSessionId}`);
            if (res.data && Array.isArray(res.data)) {
                setMessages(res.data.map((m: any) => ({
                    id: newMsgId(),
                    role: m.Role ?? m.role,
                    content: m.Content ?? m.content,
                    citations: m.Citations ?? m.citations,
                })));
            }
        } catch (e) {
            console.error('Failed to load chat history:', e);
            setChatError({
                title: 'Failed to Load Chat History',
                message: 'Unable to retrieve chat messages for this session. Please refresh or try again.',
            });
        } finally {
            setLoadingHistory(false);
        }
    };

    const startNewChat = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        setSessionId(null);
        setMessages([]);
        setChatError(null);
    };

    const startPolling = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${API_URL}/AiIndexing/progress/${memberId}`);
                const p: IndexProgress = {
                    total: res.data.Total ?? res.data.total ?? 0,
                    processed: res.data.Processed ?? res.data.processed ?? 0,
                    stage: res.data.Stage ?? res.data.stage ?? 'Working...',
                    isComplete: res.data.IsComplete ?? res.data.isComplete ?? false,
                    hasError: res.data.HasError ?? res.data.hasError ?? false,
                };
                setIndexProgress(p);
                if (p.isComplete || p.hasError) {
                    if (pollRef.current) clearInterval(pollRef.current);
                    setIsInitializing(false);
                }
            } catch {
                // polling failure - keep trying
            }
        }, 1500);
    };

    // Indexing progress polling
    useEffect(() => {
        if (!memberId || !isAiEnabled) return;

        const initializeIndex = async () => {
            try {
                await axios.post(`${API_URL}/AiIndexing/ensure-member-index/${memberId}`);
                startPolling();
            } catch (e) {
                console.error('Failed to start AI index:', e);
                setIsInitializing(false);
            }
        };

        initializeIndex();

        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [memberId, isAiEnabled]);

    const handleForceReindex = async () => {
        if (!memberId || isInitializing) return;
        setIsInitializing(true);
        setIndexProgress({ total: 0, processed: 0, stage: 'Starting re-index...', isComplete: false, hasError: false });
        try {
            await axios.post(`${API_URL}/AiIndexing/ensure-member-index/${memberId}?force=true`);
            startPolling();
        } catch (e) {
            console.error('Failed to force AI index:', e);
            setIsInitializing(false);
        }
    };

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const sendMessage = async (textQuery?: string) => {
        const queryStr = typeof textQuery === 'string' ? textQuery : input;
        const query = queryStr.trim();
        if (!query || loading) return;

        // Abort any prior in-flight stream before starting a new one
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const userMessage: ChatMessage = { id: newMsgId(), role: 'user', content: query };
        const assistantId = newMsgId();
        const assistantMessage: ChatMessage = { id: assistantId, role: 'assistant', content: '', citations: [] };
        const updatedMessages = [...messages, userMessage];
        const baseMessages = [...updatedMessages, assistantMessage];
        setMessages(baseMessages);
        setInput('');
        setLoading(true);
        setChatError(null);

        // ponytail: index-by-id is stable across concurrent setMessages calls
        const updateAssistant = (mutator: (m: ChatMessage) => ChatMessage) => {
            setMessages(prev => {
                const idx = prev.findIndex(m => m.id === assistantId);
                if (idx < 0) return prev;
                const next = [...prev];
                next[idx] = mutator(next[idx]);
                return next;
            });
        };
        const replaceAssistantWithError = (errDetails: ChatErrorDetails) => {
            setChatError(errDetails);
            updateAssistant(m => ({
                ...m,
                content: errDetails.message,
                isError: true,
                errorDetails: errDetails,
            }));
        };

        try {
            const history = messages
                .filter(m => !m.isError)
                .map(m => ({ role: m.role, content: m.content }));

            const token = readAuthToken();

            const response = await fetch(`${API_URL}/chpai/ask/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    query,
                    history,
                    memberId,
                    sessionId,
                    maxOutputTokens: 4000,
                }),
                signal: controller.signal,
            });

            if (!response.ok) {
                let errorData: any;
                try {
                    errorData = await response.json();
                } catch {
                    errorData = { error: `Server error (${response.status})` };
                }
                replaceAssistantWithError(parseAiError({ response: { status: response.status, data: errorData } }, query));
                return;
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('Streaming response body is missing.');
            }

            const decoder = new TextDecoder();
            let accumulatedContent = '';
            let currentCitations: Citation[] = [];
            let currentSessionId: string | null = sessionId;
            let buffer = '';
            let sessionRefreshed = false;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed.startsWith('data:')) continue;

                    const jsonStr = trimmed.substring(5).trim();
                    if (!jsonStr) continue;

                    try {
                        const chunk = JSON.parse(jsonStr);
                        const evtType = (chunk.EventType ?? chunk.eventType ?? '').toLowerCase();
                        const chunkSessionId = chunk.SessionId ?? chunk.sessionId;

                        if (chunkSessionId && !currentSessionId) {
                            currentSessionId = chunkSessionId;
                            setSessionId(chunkSessionId);
                        }

                        if (evtType === 'error') {
                            const errStatus = chunk.statusCode ?? chunk.StatusCode ?? 500;
                            replaceAssistantWithError(parseAiError({ response: { status: errStatus, data: chunk } }, query));
                            return;
                        } else if (evtType === 'metadata') {
                            const rawCitations = chunk.Citations ?? chunk.citations ?? [];
                            currentCitations = rawCitations.map((c: any) => ({
                                title: c.Title ?? c.title,
                                source: c.Source ?? c.source,
                                url: c.Url ?? c.url,
                                excerpt: c.Excerpt ?? c.excerpt,
                            }));
                            updateAssistant(m => ({ ...m, citations: currentCitations }));
                        } else if (evtType === 'token') {
                            const tokenContent = chunk.Content ?? chunk.content ?? '';
                            accumulatedContent += tokenContent;
                            updateAssistant(m => ({ ...m, content: accumulatedContent, citations: currentCitations }));
                        } else if (evtType === 'done') {
                            // Backend persists chat history only after the stream completes —
                            // refresh sidebar here, once, rather than on metadata (which fires pre-save).
                            if (!sessionRefreshed) {
                                sessionRefreshed = true;
                                refreshSessions();
                            }
                        }
                    } catch (e) {
                        console.error('Error parsing SSE json chunk:', e, jsonStr);
                    }
                }
            }

            // Stream ended without explicit done event — refresh anyway to be safe
            if (!sessionRefreshed) refreshSessions();
        } catch (err: any) {
            // Aborted by user navigation / new prompt — leave whatever tokens already arrived
            if (err?.name === 'AbortError') return;
            replaceAssistantWithError(parseAiError(err, query));
        } finally {
            if (abortRef.current === controller) abortRef.current = null;
            setLoading(false);
            fetchRateLimitStatus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (checkingStatus) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 112px)' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 112px)' }}>

            {/* ─── Left Sidebar: Chat History ─── */}
            <Box
                sx={{
                    width: 240,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    overflow: 'hidden',
                }}
            >
                {/* Sidebar Header */}
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.7rem' }}>
                        Chats
                    </Typography>
                    <Tooltip title="New Chat">
                        <IconButton size="small" onClick={startNewChat} sx={{ color: 'primary.main' }}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Divider />

                {/* New Chat (active unsaved) */}
                {!sessionId && (
                    <ListItem disablePadding sx={{ mb: 0.25, px: 0.5, mt: 0.5 }}>
                        <ListItemButton
                            selected
                            sx={{
                                borderRadius: 1,
                                px: 1.5,
                                py: 0.75,
                                '&.Mui-selected': { bgcolor: 'primary.50' },
                            }}
                        >
                            <ChatBubbleOutlineIcon sx={{ fontSize: 14, mr: 1, opacity: 0.5, flexShrink: 0 }} />
                            <ListItemText
                                primary="New Chat"
                                primaryTypographyProps={{ noWrap: true, variant: 'body2', fontWeight: 600 }}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                {/* Session List */}
                <List dense disablePadding sx={{ flex: 1, overflowY: 'auto', px: 0.5, pt: 0.5 }}>
                    {sessions.length === 0 && !sessionId && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                            No previous chats yet.
                        </Typography>
                    )}
                    {sessions.map((s) => (
                        <ListItem key={s.sessionId} disablePadding sx={{ mb: 0.25 }}>
                            <ListItemButton
                                onClick={() => loadSessionHistory(s.sessionId)}
                                selected={s.sessionId === sessionId}
                                sx={{
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    '&.Mui-selected': { bgcolor: 'primary.50' },
                                    '&.Mui-selected:hover': { bgcolor: 'primary.100' },
                                }}
                            >
                                <ChatBubbleOutlineIcon sx={{ fontSize: 14, mr: 1, opacity: 0.5, flexShrink: 0 }} />
                                <ListItemText
                                    primary={s.title || 'Untitled Chat'}
                                    secondary={formatSessionDate(s.lastUpdatedTime)}
                                    primaryTypographyProps={{ noWrap: true, variant: 'body2', fontWeight: s.sessionId === sessionId ? 600 : 400 }}
                                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Box>

            {/* ─── Main Chat Area ─── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.paper', zIndex: 1 }}>
                    <Box sx={{ width: 48 }} /> {/* Spacer to keep title centered */}
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                        {sessionId ? sessions.find(s => s.sessionId === sessionId)?.title || 'Chat' : 'New Chat'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                        {rateLimitStatus && isAiEnabled && (
                            <Tooltip title="View Token Usage & Rate Limits">
                                <Chip
                                    icon={<SpeedIcon fontSize="small" />}
                                    label={`${(rateLimitStatus.tokensUsedLastMinute ?? 0).toLocaleString()} / ${(rateLimitStatus.maxTokensPerMinute ?? 0).toLocaleString()} TPM`}
                                    onClick={() => setShowTokenMeter(!showTokenMeter)}
                                    size="small"
                                    variant={showTokenMeter ? 'filled' : 'outlined'}
                                    color={
                                        (rateLimitStatus.tpmUtilizationPercent ?? 0) > 90 || (rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0) > 90
                                            ? 'error'
                                            : (rateLimitStatus.tpmUtilizationPercent ?? 0) > 70 || (rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0) > 70
                                            ? 'warning'
                                            : 'default'
                                    }
                                    sx={{ fontWeight: 600, fontSize: '0.725rem', cursor: 'pointer' }}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title={isAiEnabled ? "Chat Settings" : "Chat Settings (Disabled)"}>
                            <IconButton
                                size="small"
                                onClick={handleOpenSettings}
                                disabled={!isAiEnabled}
                                color="primary"
                            >
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {hasReindexPermission && (
                            <Tooltip title={isAiEnabled ? "Force Re-index Chart Data" : "Force Re-index (Disabled)"}>
                                <IconButton 
                                    size="small" 
                                    onClick={handleForceReindex} 
                                    disabled={isInitializing || !isAiEnabled}
                                    color="primary"
                                >
                                    <SmartToyIcon fontSize="small" sx={{ opacity: (isInitializing || !isAiEnabled) ? 0.5 : 1 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {/* Token Meter Dashboard Banner */}
                {showTokenMeter && rateLimitStatus && (
                    <Paper
                        elevation={1}
                        sx={{
                            mx: 2,
                            mt: 1.5,
                            mb: 0,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SpeedIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle2" fontWeight={700}>
                                    AI Rate Limits & Token Progress
                                </Typography>
                                <Chip
                                    label={`Tier: ${rateLimitStatus.effectiveSource}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                            </Box>
                            <IconButton size="small" onClick={() => setShowTokenMeter(false)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Grid container spacing={2}>
                            {/* Tokens Per Minute (TPM) */}
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Box sx={{ p: 1.25, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                            Tokens (Last 60s)
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color={rateLimitStatus.tpmUtilizationPercent > 90 ? 'error.main' : rateLimitStatus.tpmUtilizationPercent > 70 ? 'warning.main' : 'text.primary'}>
                                            {rateLimitStatus.tpmUtilizationPercent}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={rateLimitStatus.tpmUtilizationPercent}
                                        color={rateLimitStatus.tpmUtilizationPercent > 90 ? 'error' : rateLimitStatus.tpmUtilizationPercent > 70 ? 'warning' : 'primary'}
                                        sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {(rateLimitStatus.tokensUsedLastMinute ?? 0).toLocaleString()} / {(rateLimitStatus.maxTokensPerMinute ?? 0).toLocaleString()} TPM
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Requests Per Minute (RPM) */}
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Box sx={{ p: 1.25, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                            Requests (Last 60s)
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color={(rateLimitStatus.rpmUtilizationPercent ?? 0) > 90 ? 'error.main' : (rateLimitStatus.rpmUtilizationPercent ?? 0) > 70 ? 'warning.main' : 'text.primary'}>
                                            {rateLimitStatus.rpmUtilizationPercent ?? 0}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={rateLimitStatus.rpmUtilizationPercent ?? 0}
                                        color={(rateLimitStatus.rpmUtilizationPercent ?? 0) > 90 ? 'error' : (rateLimitStatus.rpmUtilizationPercent ?? 0) > 70 ? 'warning' : 'primary'}
                                        sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {rateLimitStatus.requestsLastMinute ?? 0} / {rateLimitStatus.maxRequestsPerMinute ?? 0} RPM
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Monthly Token Budget */}
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Box sx={{ p: 1.25, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                            Monthly Token Budget
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color={(rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0) > 90 ? 'error.main' : (rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0) > 70 ? 'warning.main' : 'text.primary'}>
                                            {rateLimitStatus.maxTokenBudgetPerMonth > 0 ? `${rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0}%` : 'Unlimited'}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={rateLimitStatus.maxTokenBudgetPerMonth > 0 ? (rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0) : 0}
                                        color={(rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0) > 90 ? 'error' : (rateLimitStatus.monthlyBudgetUtilizationPercent ?? 0) > 70 ? 'warning' : 'info'}
                                        sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {(rateLimitStatus.tokensUsedThisMonth ?? 0).toLocaleString()} / {rateLimitStatus.maxTokenBudgetPerMonth > 0 ? (rateLimitStatus.maxTokenBudgetPerMonth ?? 0).toLocaleString() : '∞'} Tokens
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2, maxWidth: 820, width: '100%', mx: 'auto' }}>

                {/* Messages */}
                <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
                    {isInitializing ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, px: 4 }}>
                            <CircularProgress size={36} />
                            <Typography variant="body1" fontWeight={500} color="text.primary">
                                {indexProgress.stage === 'Calculating records...' || indexProgress.total === 0
                                    ? 'Calculating member history...'
                                    : `Indexing: ${indexProgress.stage}`}
                            </Typography>
                            {indexProgress.total > 0 && (
                                <Box sx={{ width: '100%', maxWidth: 420 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(100, Math.round((indexProgress.processed / indexProgress.total) * 100))}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {indexProgress.processed} of {indexProgress.total} records
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {Math.min(100, Math.round((indexProgress.processed / indexProgress.total) * 100))}%
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            {indexProgress.hasError && (
                                <Typography variant="caption" color="error">An error occurred. You may still ask questions.</Typography>
                            )}
                        </Box>
                    ) : loadingHistory ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                            <CircularProgress />
                        </Box>
                    ) : messages.length === 0 && !loading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1, color: 'text.secondary' }}>
                            <SmartToyIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                            <Typography variant="body2" sx={{ mb: 1 }}>Ask anything about {memberName}'s chart.</Typography>

                            {loadingSuggestions ? (
                                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ maxWidth: 600, mt: 2 }}>
                                    <Skeleton variant="rounded" width={180} height={32} sx={{ borderRadius: 16 }} />
                                    <Skeleton variant="rounded" width={150} height={32} sx={{ borderRadius: 16 }} />
                                    <Skeleton variant="rounded" width={200} height={32} sx={{ borderRadius: 16 }} />
                                </Stack>
                            ) : (
                                <Box sx={{ width: '100%', maxWidth: 650 }}>
                                    {suggestions.recentEvents.length > 0 && (
                                        <>
                                            <Typography variant="overline" sx={{ mt: 1, mb: 0.5, display: 'block', textAlign: 'center', color: 'primary.main', fontWeight: 700, letterSpacing: 1.2 }}>
                                                Based on Recent Events
                                            </Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ mb: 3 }}>
                                                {suggestions.recentEvents.map((s, idx) => (
                                                    <Chip
                                                        key={`recent-${idx}`}
                                                        label={s}
                                                        onClick={() => sendMessage(s)}
                                                        variant="outlined"
                                                        color="primary"
                                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
                                                    />
                                                ))}
                                            </Stack>
                                        </>
                                    )}

                                    {suggestions.quickAsks.length > 0 && (
                                        <>
                                            <Typography variant="overline" sx={{ mb: 0.5, display: 'block', textAlign: 'center', opacity: 0.6, fontWeight: 700, letterSpacing: 1.2 }}>
                                                Quick Asks
                                            </Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                                                {suggestions.quickAsks.map((s, idx) => (
                                                    <Chip
                                                        key={`quick-${idx}`}
                                                        label={s}
                                                        onClick={() => sendMessage(s)}
                                                        variant="outlined"
                                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
                                                    />
                                                ))}
                                            </Stack>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {messages.map((msg, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                                gap: 1,
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box
                                sx={{
                                    mt: 0.5,
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    bgcolor: msg.role === 'user' ? 'primary.main' : msg.isError ? 'error.50' : 'grey.200',
                                    color: msg.role === 'user' ? 'white' : msg.isError ? 'error.main' : 'text.secondary',
                                    border: msg.isError ? '1px solid' : 'none',
                                    borderColor: msg.isError ? 'error.light' : 'transparent',
                                }}
                            >
                                {msg.role === 'user' ? (
                                    <PersonIcon sx={{ fontSize: 18 }} />
                                ) : msg.isError ? (
                                    <ReportProblemIcon sx={{ fontSize: 18 }} />
                                ) : (
                                    <SmartToyIcon sx={{ fontSize: 18 }} />
                                )}
                            </Box>

                            <Box sx={{ maxWidth: '80%', flex: msg.isError ? 1 : undefined }}>
                                {msg.isError ? (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            px: 2,
                                            py: 1.75,
                                            bgcolor: '#fff5f5',
                                            border: '1px solid #f44336',
                                            borderRadius: '2px 12px 12px 12px',
                                            color: 'text.primary',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', mb: 0.75 }}>
                                            <ReportProblemIcon fontSize="small" />
                                            <Typography variant="subtitle2" fontWeight={700} color="error.main">
                                                {msg.errorDetails?.title || 'Message Delivery Failed'}
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                            {msg.content}
                                        </Typography>

                                        {msg.errorDetails?.reason && (
                                            <Box
                                                sx={{
                                                    my: 1,
                                                    p: 1.25,
                                                    bgcolor: 'rgba(211, 47, 47, 0.05)',
                                                    borderRadius: 1,
                                                    borderLeft: '3px solid #d32f2f',
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={700} color="error.dark" sx={{ display: 'block', mb: 0.25 }}>
                                                    Guardrail Explanation:
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.825rem' }}>
                                                    "{msg.errorDetails.reason}"
                                                </Typography>
                                            </Box>
                                        )}

                                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: msg.errorDetails?.query ? 1.5 : 0 }}>
                                            {msg.errorDetails?.category && (
                                                <Chip
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    label={`Category: ${formatCategoryLabel(msg.errorDetails.category)}`}
                                                    sx={{ fontWeight: 600, fontSize: '0.725rem' }}
                                                />
                                            )}
                                            {msg.errorDetails?.confidence != null && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`Confidence: ${Math.round(msg.errorDetails.confidence * 100)}%`}
                                                    sx={{ fontSize: '0.725rem' }}
                                                />
                                            )}
                                            {msg.errorDetails?.retryAfterSeconds != null && (
                                                <Chip
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    label={`Retry in: ${msg.errorDetails.retryAfterSeconds}s`}
                                                    sx={{ fontWeight: 600, fontSize: '0.725rem' }}
                                                />
                                            )}
                                            {msg.errorDetails?.code && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`HTTP ${msg.errorDetails.code}`}
                                                    sx={{ fontSize: '0.725rem' }}
                                                />
                                            )}
                                        </Stack>

                                        {msg.errorDetails?.query && (
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                                startIcon={<ReplayIcon fontSize="small" />}
                                                onClick={() => sendMessage(msg.errorDetails?.query)}
                                                sx={{ borderRadius: 1.5, textTransform: 'none', mt: 0.5 }}
                                            >
                                                Retry Prompt
                                            </Button>
                                        )}
                                    </Paper>
                                ) : (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                                            color: msg.role === 'user' ? 'white' : 'text.primary',
                                            borderRadius: msg.role === 'user' ? '12px 2px 12px 12px' : '2px 12px 12px 12px',
                                            '& p': { margin: '0 0 8px', '&:last-child': { marginBottom: 0 } },
                                            '& ul, & ol': { margin: '0 0 8px', paddingLeft: '20px' },
                                            '& li': { marginBottom: '2px' },
                                            '& strong': { fontWeight: 600 },
                                            '& code': { fontFamily: 'monospace', fontSize: '0.85em', bgcolor: 'rgba(0,0,0,0.07)', px: 0.5, borderRadius: '3px' },
                                            '& pre': { bgcolor: 'rgba(0,0,0,0.07)', p: 1, borderRadius: '4px', overflowX: 'auto', '& code': { bgcolor: 'transparent', px: 0 } },
                                            '& h1, & h2, & h3': { mt: 1, mb: 0.5, fontWeight: 600 },
                                        }}
                                    >
                                        {msg.role === 'user' ? (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                                        ) : !msg.content ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.5 }}>
                                                <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                    Thinking & searching context...
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" component="div">
                                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                                            </Typography>
                                        )}
                                    </Paper>
                                )}

                                {msg.citations && msg.citations.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <details>
                                            <summary style={{ fontSize: '0.75rem', color: 'gray', cursor: 'pointer', outline: 'none' }}>
                                                View {msg.citations.length} sources
                                            </summary>
                                            <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
                                                {msg.citations.map((c, ci) => (
                                                    <Tooltip key={ci} title={c.excerpt ?? ''} placement="top" arrow>
                                                        <Link
                                                            href={c.url}
                                                            target="_blank"
                                                            rel="noopener"
                                                            underline="hover"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                px: 1,
                                                                py: 0.25,
                                                                borderRadius: 1,
                                                                bgcolor: 'grey.200',
                                                                color: 'text.secondary',
                                                                display: 'inline-block',
                                                                '&:hover': { bgcolor: 'grey.300' },
                                                            }}
                                                        >
                                                            {c.source}: {c.title}
                                                        </Link>
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        </details>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}

                    <div ref={bottomRef} />
                </Box>

                {/* Input */}
                <Box sx={{ pt: 1 }}>  
                    {!isAiEnabled && (
                        <Alert severity="warning" sx={{ mb: 1, borderRadius: 1 }}>
                            Chart Assistant is currently disabled by an administrator.
                        </Alert>
                    )}
                    {chatError && (
                        <Alert
                            severity="error"
                            onClose={() => setChatError(null)}
                            action={
                                chatError.query ? (
                                    <Button
                                        color="error"
                                        size="small"
                                        startIcon={<ReplayIcon fontSize="small" />}
                                        onClick={() => { setChatError(null); sendMessage(chatError.query); }}
                                    >
                                        Retry
                                    </Button>
                                ) : undefined
                            }
                            sx={{ mb: 1, borderRadius: 1.5 }}
                        >
                            <Typography variant="subtitle2" fontWeight={700}>{chatError.title}</Typography>
                            <Typography variant="body2">{chatError.message}</Typography>
                            {chatError.reason && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', opacity: 0.9 }}>
                                    Reason: {chatError.reason}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                {chatError.category && (
                                    <Chip size="small" color="error" variant="outlined" label={`Category: ${formatCategoryLabel(chatError.category)}`} />
                                )}
                                {chatError.confidence != null && (
                                    <Chip size="small" variant="outlined" label={`Confidence: ${Math.round(chatError.confidence * 100)}%`} />
                                )}
                                {chatError.retryAfterSeconds != null && (
                                    <Chip size="small" color="warning" variant="outlined" label={`Retry in: ${chatError.retryAfterSeconds}s`} />
                                )}
                                {chatError.code && (
                                    <Chip size="small" variant="outlined" label={`HTTP ${chatError.code}`} />
                                )}
                            </Stack>
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder={isAiEnabled ? `Ask anything about ${memberName}'s chart...` : "Chart Assistant is disabled"}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={loading || isInitializing || !isAiEnabled}
                        size="small"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => sendMessage()}
                                        disabled={loading || isInitializing || !input.trim() || !isAiEnabled}
                                        sx={{ minWidth: 0, px: 1.5 }}
                                    >
                                        <SendIcon fontSize="small" />
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {isAiEnabled ? "Press Enter to send · Shift+Enter for new line" : ""}
                    </Typography>
                </Box>
            </Box>
            </Box>

            {/* Settings Dialog */}
            <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>AI Chat Instructions</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These instructions will be appended to the AI's internal prompt to customize its responses.
                    </Typography>
                    
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Global Instructions (Applies to all members)
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="e.g. Always format your responses in bullet points. I prefer concise answers."
                        value={instructions.global}
                        onChange={(e) => setInstructions({ ...instructions, global: e.target.value })}
                        sx={{ mb: 3 }}
                    />
                    
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Member-Specific Instructions (Applies only to {memberName})
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={`e.g. ${memberName} has a history of X. Always check their latest Y reading.`}
                        value={instructions.member}
                        onChange={(e) => setInstructions({ ...instructions, member: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveSettings} disabled={savingSettings}>
                        {savingSettings ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MemberChatPage;
